import { phoneFormatter } from '@asn/formatters';

import { AfterViewInit, Component, OnChanges, SimpleChanges, ViewEncapsulation, input, inject } from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors, FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';

import { ApContentService } from '@ap/content';
import { AbstractInputComponent, InputType, InputmodeType, ApAutocompleteFilterService, ApAutocompleteModule } from '@ap/input';
import { ApInputConstraintService } from '@ap/input/constraint';
import { ApPhoneValidator } from '@ap/input/validators';
import { ApSuggestionComponent } from '@ap/input/suggestion';
import { ApFormErrorsComponent } from '@ap/form/errors';
import { ApIconComponent } from '@ap/icon';

import content from './phone-input.content.json';
import { PhoneNumberFormat } from './phone-number-format.enum';

@Component({
    selector: 'ap-phone-input',
    templateUrl: '../../../src/lib/input.component.html',
    styleUrls: ['../../../src/lib/input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, ApIconComponent, ApSuggestionComponent, ApFormErrorsComponent, ApAutocompleteModule, NgTemplateOutlet],
    providers: [ApInputConstraintService]
})
export class ApPhoneInputComponent extends AbstractInputComponent implements AfterViewInit, OnChanges {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);
    protected readonly filterService = inject(ApAutocompleteFilterService);

    /**
     * Formatting of the phone number.
     * Can be NL_PHONE, NL_MOBILE or INTERNATIONAL
     */
    public readonly numberFormat = input(PhoneNumberFormat.NL_PHONE);

    /**
     * Store current validator to be able to remove from control
     */
    private _phoneTypeValidator: (control: AbstractControl) => ValidationErrors;

    /**
     * Maxlength of a phone number (readable formatted international)
     */
    protected readonly maxlength = input<number>(17);

    /**
     * Inputmode (Standard HTML inputmode types)
     */
    protected readonly inputmode = input<InputmodeType>('tel');

    /**
     * type is tel
     */
    protected readonly type = input<InputType>('tel');

    /**
     * Characters that are stripped
     */
    protected readonly stripPattern = input<RegExp>(/[^\d\s-+]/g);

    constructor() {
        super();
        this.setDefaultContent(content, 'phone-input');
    }

    /**
     * After view init set validation
     */
    public ngAfterViewInit(): void {
        this.setValidation();
    }

    /**
     * If number format changes call set validation
     */
    public ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['numberFormat'] !== undefined) {
            this.setValidation();
        }
    }

    /**
     * Remove previous phoneFormat validation and add new one.
     */
    private setValidation(): void {
        const control = this.control?.control;
        if (control) {
            const previousValidator = this._phoneTypeValidator;
            const numberFormat = this.numberFormat();
            if (numberFormat === PhoneNumberFormat.NL_PHONE) {
                this._phoneTypeValidator = ApPhoneValidator.validateDutch;
            } else if (numberFormat === PhoneNumberFormat.NL_MOBILE) {
                this._phoneTypeValidator = ApPhoneValidator.validateDutchMobile;
            } else if (numberFormat === PhoneNumberFormat.INTERNATIONAL) {
                this._phoneTypeValidator = ApPhoneValidator.validateInternational;
            }
            if (this._phoneTypeValidator !== previousValidator) {
                if (!!previousValidator) {
                    control.removeValidators(previousValidator);
                }
                control.addValidators([this._phoneTypeValidator]);
                control.updateValueAndValidity();
            }
        }
    }

    /**
     * Format value as phone number
     * Called on blur of input
     */
    protected override formatValue(value: string): string {
        return phoneFormatter.format(value);
    }

    /**
     * Format phone number to human-readable string
     */
    protected override formatValueDisplay(value: string): string {
        return phoneFormatter.formatDisplay(value);
    }
}
